import {
  CloudUpload,
  CreditCardOutlined,
  DeleteOutline,
  FolderOpenOutlined,
  Inbox,
  LocalHospitalOutlined,
  PersonOutline,
  PictureAsPdfOutlined,
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { isCashPayment } from "./constants";
import { buildUploadFilesFormData, formatCentralDateTime } from "./pureLogic";
import * as api from "./billCollection.service";

const isViewerOnly = () => localStorage.getItem("viewer") === "true";

/**
 * Document Upload tab — mirrors the Upload pane of central_dashboard's
 * `BillCollectionModalComponent`.
 *
 * Presentational: uploaded files are only STAGED (reported to the parent through
 * `onFilesAdded` / `onDelete`). Nothing is written to the DB here. The parent
 * commits everything when "Send for Approval" succeeds its first step.
 */
const DocumentUploadTab = ({
  data,
  finalImgArray,
  disableFileUpload,
  uploadFinalBillAmount,
  setUploadFinalBillAmount,
  uploadNetBillAmount,
  setUploadNetBillAmount,
  showAmountNote,
  isSubmitDisabled,
  onFilesAdded,
  onDelete,
  onSubmit,
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragover, setDragover] = useState(false);
  const fileInputRef = useRef(null);

  const paymentMode = data?.payment_mode || "";
  const isCash = isCashPayment(paymentMode);
  const appId = data?.appID || "";
  const userObj = JSON.parse(localStorage.getItem("user") || "{}") || {};
  const userName =
    userObj?.username || userObj?.name || localStorage.getItem("userName") || "";
  const userId = userObj?.userId || localStorage.getItem("userId") || "";

  const handleFiles = async (files) => {
    if (isViewerOnly()) {
      toast.warning("You are only allowed to view.");
      return;
    }
    if (!files?.length || disableFileUpload) return;
    setUploading(true);
    try {
      const formData = buildUploadFilesFormData(files, appId);
      const res = await api.uploadFiles(formData);
      if (res?.status === 200 && res?.data?.length) {
        onFilesAdded(
          res.data.map((item) => ({
            user: userId,
            userName,
            dateTimeISO: new Date().toISOString(),
            url: item.Url,
            name: item.fileName,
          }))
        );
      }
    } catch (err) {
      console.error(err);
      toast.error("File upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragover(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDelete = (index) => {
    if (isViewerOnly()) {
      toast.warning("You are only allowed to view.");
      return;
    }
    onDelete(index);
  };

  const downloadFile = async (url, name) => {
    try {
      const res = await api.getSignedUrl([url]);
      const signedUrl = res?.result?.[0] || res?.[0] || url;
      const link = document.createElement("a");
      link.href = signedUrl;
      link.download = name || "unknown";
      link.target = "_blank";
      link.click();
    } catch {
      toast.error("Download failed");
    }
  };

  const count = finalImgArray?.length || 0;

  return (
    <div className="bcm-upload-pane">
      <div className="bcm-upload-grid">
        {/* LEFT: Uploaded documents list */}
        <div className="bcm-upload-left">
          <div className="bcm-upload-left-card">
            <div className="bcm-upload-left-head">
              <FolderOpenOutlined sx={{ fontSize: 17 }} />
              Uploaded Documents
              {count > 0 && <span className="bcm-badge-sm">{count}</span>}
            </div>

            {count > 0 ? (
              <div className="bcm-file-list">
                {finalImgArray.map((file, i) => (
                  <div className="bcm-file-item" key={(file.url || "") + i}>
                    <div className="bcm-file-info">
                      <div className="bcm-file-thumb">
                        <PictureAsPdfOutlined sx={{ fontSize: 17 }} />
                      </div>
                      <div className="bcm-file-meta">
                        <span
                          className="bcm-file-name"
                          onClick={() => downloadFile(file.url, file.name)}
                        >
                          Bill {i + 1}
                        </span>
                        <span className="bcm-file-date">
                          {formatCentralDateTime(file.dateTimeISO)}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="bcm-file-delete"
                      onClick={() => handleDelete(i)}
                      title="Remove"
                    >
                      <DeleteOutline sx={{ fontSize: 17 }} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bcm-upload-empty">
                <Inbox className="bcm-empty-icon" />
                <span className="bcm-empty-title">No documents yet</span>
                <span className="bcm-empty-sub">
                  Upload your first document using the panel on the right.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Upload controls */}
        <div className="bcm-upload-right">
          <div className="bcm-upload-right-card">
            {/* Patient info strip (dark) */}
            <div className="bcm-patient-strip">
              <div className="bcm-patient-item">
                <PersonOutline sx={{ fontSize: 14 }} />
                <span>{data?.name || data?.patient_name || "-"}</span>
              </div>
              <div className="bcm-patient-item">
                <LocalHospitalOutlined sx={{ fontSize: 14 }} />
                <span>{data?.hospital || data?.hospital_name || "-"}</span>
              </div>
              <div className="bcm-patient-item">
                <CreditCardOutlined sx={{ fontSize: 14 }} />
                <span>{paymentMode || "-"}</span>
              </div>
            </div>

            <div className="bcm-upload-right-head">
              <CloudUpload sx={{ fontSize: 17 }} />
              Upload New Document
            </div>

            {/* Amount fields (Cash only) — entry-only, always start empty */}
            {isCash && showAmountNote && (
              <div className="bcm-amount-note">
                Final Bill Amount and Net Bill Amount are mandatory to submit.
              </div>
            )}
            {isCash && (
              <div className="bcm-field-row">
                <div className="bcm-field-group">
                  <label>Final Bill Amount</label>
                  <input
                    type="number"
                    className="bcm-input"
                    value={uploadFinalBillAmount}
                    onChange={(e) => setUploadFinalBillAmount(e.target.value)}
                    placeholder="Enter amount"
                  />
                </div>
                <div className="bcm-field-group">
                  <label>Net Bill Amount</label>
                  <input
                    type="number"
                    className="bcm-input"
                    value={uploadNetBillAmount}
                    onChange={(e) => setUploadNetBillAmount(e.target.value)}
                    placeholder="Enter amount"
                  />
                </div>
              </div>
            )}

            {/* Drag/drop zone */}
            {!disableFileUpload && (
              <div
                className={`bcm-dropzone ${dragover ? "dragover" : ""}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragover(true);
                }}
                onDragLeave={() => setDragover(false)}
                onDrop={handleDrop}
              >
                {uploading ? (
                  <CircularProgress size={26} />
                ) : (
                  <>
                    <CloudUpload className="bcm-drop-icon" />
                    <span className="bcm-drop-title">Drag &amp; drop files here</span>
                    <span className="bcm-drop-sub">
                      or click to browse from your computer
                    </span>
                  </>
                )}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*, .pdf, .doc,.docx,.odt"
              style={{ display: "none" }}
              onChange={(e) => handleFiles(e.target.files)}
            />

            {/* Footer: for Cash both amounts must be filled (after uploading the
                document) before the button is enabled. */}
            <div className="bcm-upload-footer">
              <button
                type="button"
                className="bcm-btn-primary"
                onClick={onSubmit}
                disabled={isSubmitDisabled}
              >
                Go to Document Collection Actions
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadTab;
